import { getScrollBehavior } from 'core/utils/motion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Loading } from 'ui';
import ChatMessages from '../../../../components/atoms/ChatMessages/ChatMessages';
import teacherReviewService from '../../../../services/teacherReviewService';
import type { FlaggedMessageAnnotation } from '../../../../types/radar';
import { transformApiMessagesToUI } from '../../../../utils/messageTransformer';
import { getMockConversations } from '../../../../mocks/mockRadarData';

type UIMessage = {
  attachments: Array<{
    contentType: string;
    createdAt: string;
    fileName: string;
    sizeInBytes: number;
    url: string;
  }>;
  context: Record<string, unknown>;
  id: string;
  isFlagged: boolean;
  sender: 'user' | 'bot';
  text: string;
  timestamp: Date;
};

type InappropriateUseContentProps = {
  isMockMode?: boolean;
  raw: FlaggedMessageAnnotation;
};

function InappropriateUseContent({ isMockMode, raw }: InappropriateUseContentProps): JSX.Element {
  const { i18n, t } = useTranslation(['chatbot']);
  const contentRef = useRef<HTMLDivElement>(null);
  const [conversation, setConversation] = useState<UIMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { categories, conversationId, messageId } = raw;

  useEffect(() => {
    if (!conversationId) return;

    if (isMockMode) {
      const mockMessages = getMockConversations(i18n.language)[conversationId];
      if (mockMessages) {
        setConversation(
          mockMessages.map((msg) => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
          })) as UIMessage[]
        );
      } else {
        setConversation([]);
      }
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchConversation(): Promise<void> {
      setLoading(true);
      setError(null);

      try {
        const data = await teacherReviewService.getConversation(conversationId);
        if (!cancelled) {
          setConversation(transformApiMessagesToUI(data.messages) as UIMessage[]);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          setConversation([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchConversation();

    return () => {
      cancelled = true;
    };
  }, [conversationId, isMockMode]);

  const scrollToFlaggedMessage = useCallback(() => {
    if (!messageId || !contentRef.current) return;

    const flaggedElement = contentRef.current.querySelector(`[data-message-id="${messageId}"]`);
    if (flaggedElement) {
      flaggedElement.scrollIntoView({ behavior: getScrollBehavior(), block: 'center' });
    }
  }, [messageId]);

  useEffect(() => {
    if (conversation.length > 0) {
      const timer = setTimeout(scrollToFlaggedMessage, 100);
      return () => clearTimeout(timer);
    }
  }, [conversation, scrollToFlaggedMessage]);

  const enrichedConversation = useMemo(() => {
    if (!messageId || !conversation.length) return conversation;
    return conversation.map((msg) => {
      if (msg.id === messageId) {
        return { ...msg, highlighted: true, flagCategories: categories };
      }
      return msg;
    });
  }, [conversation, messageId, categories]);

  if (loading) {
    return (
      <div className="alert-detail-drawer__loading">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert-detail-drawer__error">
        <p>{t('chatbot:ai_analytics_student_conversation_error')}</p>
      </div>
    );
  }

  if (conversation.length === 0) {
    return (
      <div className="alert-detail-drawer__empty">
        <p>{t('chatbot:ai_analytics_student_conversation_empty')}</p>
      </div>
    );
  }

  return (
    <div className="alert-detail-drawer__messages" ref={contentRef}>
      <ChatMessages messages={enrichedConversation} readOnly />
    </div>
  );
}

export default InappropriateUseContent;
